import { useRef, useState, type DragEvent } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Bug, Sparkles, MessageSquare, AlertTriangle,
  Paperclip, X, Loader2, Camera,
} from 'lucide-react';
import { ScreenshotCropper, canCaptureScreen } from './ScreenshotCropper';
import {
  submitFeedback,
  uploadAttachment,
  getBrowserMetadata,
  getApiBase,
  ALLOWED_ATTACHMENT_TYPES,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  type FeedbackType,
  type UploadedAttachment,
} from '@membersyncpro/feedback';

const TYPES: Array<{
  type: FeedbackType;
  Icon: typeof Bug;
  label: string;
  placeholder: string;
}> = [
  { type: 'bug',             Icon: Bug,            label: 'Bug Report',       placeholder: 'What happened? What did you expect to happen?' },
  { type: 'feature_request', Icon: Sparkles,       label: 'Feature Request',  placeholder: 'What would you like to be able to do?' },
  { type: 'general',         Icon: MessageSquare,  label: 'General Feedback', placeholder: "What's on your mind?" },
  { type: 'complaint',       Icon: AlertTriangle,  label: 'Complaint',        placeholder: 'Tell us what went wrong.' },
];

const ACCEPT_STR = ALLOWED_ATTACHMENT_TYPES.join(',');

type Step = 'closed' | 'type' | 'form' | 'done';

export function FeedbackWidget() {
  const { getToken, isSignedIn } = useAuth();
  const [step, setStep] = useState<Step>('closed');
  const [type, setType] = useState<FeedbackType>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function resetState() {
    setType('general');
    setSubject('');
    setMessage('');
    setAttachments([]);
    setError(null);
    setStep('closed');
  }

  function selectType(t: FeedbackType) {
    setType(t);
    setStep('form');
    setError(null);
  }

  // ── Upload pipeline ────────────────────────────────────────────────────
  async function uploadFiles(files: File[]) {
    const remaining = MAX_ATTACHMENTS - attachments.length;
    const accepted: File[] = [];
    for (const f of files.slice(0, remaining)) {
      if (!ALLOWED_ATTACHMENT_TYPES.includes(f.type)) {
        setError(`Unsupported file type: ${f.type || f.name}`);
        continue;
      }
      if (f.size > MAX_ATTACHMENT_BYTES) {
        setError(`${f.name} is too large (max 5MB)`);
        continue;
      }
      accepted.push(f);
    }
    if (!accepted.length) return;

    setUploadingCount((c) => c + accepted.length);
    setError(null);

    await Promise.all(
      accepted.map(async (file) => {
        try {
          const bearer = isSignedIn ? await getToken() : null;
          const uploaded = await uploadAttachment(file, {
            kind: 'attached',
            ...(bearer ? { bearerToken: bearer } : {}),
          });
          setAttachments((prev) => [...prev, uploaded]);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Upload failed');
        } finally {
          setUploadingCount((c) => c - 1);
        }
      }),
    );
  }

  function removeAttachment(id: string) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  // ── Screenshot → upload as a screenshot-kind attachment ────────────────
  async function handleCroppedScreenshot(blob: Blob) {
    setCapturing(false);
    if (attachments.length >= MAX_ATTACHMENTS) {
      setError(`Maximum ${MAX_ATTACHMENTS} attachments reached`);
      return;
    }
    setUploadingCount((c) => c + 1);
    setError(null);
    try {
      const bearer = isSignedIn ? await getToken() : null;
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const uploaded = await uploadAttachment(blob, {
        kind: 'screenshot',
        filename: `screenshot-${ts}.png`,
        ...(bearer ? { bearerToken: bearer } : {}),
      });
      setAttachments((prev) => [...prev, uploaded]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Screenshot upload failed');
    } finally {
      setUploadingCount((c) => c - 1);
    }
  }

  // ── Drag and drop ──────────────────────────────────────────────────────
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (!e.dataTransfer.files?.length) return;
    void uploadFiles(Array.from(e.dataTransfer.files));
  }
  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }
  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }

  // ── Submit ─────────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || submitting || uploadingCount > 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const bearer = isSignedIn ? await getToken() : null;
      const subjectTrimmed = subject.trim();
      await submitFeedback(
        {
          type,
          app: 'web_dashboard',
          ...(subjectTrimmed ? { subject: subjectTrimmed } : {}),
          message: message.trim(),
          context: window.location.pathname,
          metadata: getBrowserMetadata(),
          ...(attachments.length ? { attachments: attachments.map((a) => ({ id: a.id })) } : {}),
        },
        bearer ? { bearerToken: bearer } : undefined,
      );
      setStep('done');
      setTimeout(() => resetState(), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  // Trigger
  if (step === 'closed') {
    return (
      <button
        onClick={() => setStep('type')}
        className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm shadow-lg hover:bg-primary/90"
        aria-label="Send feedback"
      >
        <MessageSquare className="h-4 w-4" />
        Feedback
      </button>
    );
  }

  // While the screenshot capture is in flight, hide the feedback modal
  // entirely so the user sees the underlying page (which is what they're
  // trying to capture). The ScreenshotCropper handles its own visibility:
  // it renders nothing during the picker phase, then the crop UI after.
  if (capturing) {
    return (
      <ScreenshotCropper
        onCropped={handleCroppedScreenshot}
        onCancel={() => setCapturing(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-md bg-card text-card-foreground rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-base font-semibold">
            {step === 'type' && 'Send feedback'}
            {step === 'form' && TYPES.find((t) => t.type === type)?.label}
            {step === 'done' && 'Thank you'}
          </h2>
          <button onClick={resetState} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Type chooser */}
        {step === 'type' && (
          <div className="p-2 grid grid-cols-2 gap-2">
            {TYPES.map((t) => (
              <button
                key={t.type}
                onClick={() => selectType(t.type)}
                className="flex flex-col items-center gap-2 rounded-md border p-4 hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <t.Icon className="h-6 w-6" />
                <span className="text-sm font-medium">{t.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Form */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Subject (optional)</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={120}
                className="w-full rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={4}
                maxLength={4000}
                placeholder={TYPES.find((t) => t.type === type)?.placeholder}
                className="w-full rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>

            {/* Capture (big) + Attach (small) side-by-side. Drag-drop wraps both. */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Attachments ({attachments.length}/{MAX_ATTACHMENTS})
              </label>
              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                className={
                  'flex gap-2 transition-colors rounded ' +
                  (dragOver ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : '')
                }
              >
                {/* Big primary: screen capture */}
                {canCaptureScreen() && (
                  <button
                    type="button"
                    onClick={() => setCapturing(true)}
                    disabled={attachments.length >= MAX_ATTACHMENTS || uploadingCount > 0}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                  >
                    <Camera className="h-5 w-5" />
                    Capture screen
                  </button>
                )}

                {/* Small secondary: file picker */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachments.length >= MAX_ATTACHMENTS || uploadingCount > 0}
                  className="inline-flex items-center justify-center rounded-md border bg-background hover:bg-accent hover:text-accent-foreground px-3 disabled:opacity-50"
                  aria-label="Attach file"
                  title="Attach file (PNG/JPG/WEBP/GIF, ≤5MB)"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_STR}
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length) void uploadFiles(Array.from(e.target.files));
                    e.target.value = '';
                  }}
                />
              </div>
              {dragOver && (
                <p className="mt-1 text-xs text-primary">Drop images to attach…</p>
              )}

              {/* Attached file chips */}
              {(attachments.length > 0 || uploadingCount > 0) && (
                <ul className="mt-2 space-y-1.5">
                  {attachments.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 rounded border bg-background px-2 py-1.5 text-xs">
                      <img
                        src={`${getApiBase()}${a.url}`}
                        alt=""
                        className="h-8 w-8 rounded object-cover bg-muted"
                      />
                      <span className="flex-1 truncate" title={a.filename}>{a.filename}</span>
                      <span className="text-muted-foreground tabular-nums">{Math.ceil(a.size / 1024)}KB</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.id)}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label={`Remove ${a.filename}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                  {Array.from({ length: uploadingCount }).map((_, i) => (
                    <li key={`up-${i}`} className="flex items-center gap-2 rounded border bg-background px-2 py-1.5 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Uploading…
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setStep('type')}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                ← Change type
              </button>
              <button
                type="submit"
                disabled={submitting || !message.trim() || uploadingCount > 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {submitting ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </form>
        )}

        {/* Done */}
        {step === 'done' && (
          <div className="p-8 text-center space-y-2">
            <div className="mx-auto h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <MessageSquare className="h-6 w-6 text-green-700" />
            </div>
            <p className="text-sm">Thanks — we got it.</p>
            <p className="text-xs text-muted-foreground">A human (and sometimes a fast AI) reads every submission.</p>
          </div>
        )}
      </div>

      {/* (capturing case handled at the top of the component) */}
    </div>
  );
}
